import type { ComponentChildren } from 'preact'
import { useState } from 'preact/hooks'
import { ArrowRight, Ban, ExternalLink, FileText, Lock } from 'lucide-preact'
import type { QuizDto, WebViewDto } from '@/api/webs'
import { CopyButton } from '@/components/common/CopyButton'
import { EditButton } from './EditButton'
import { QuizHeading } from './QuizHeading'
import { QuizBandStrip } from './QuizBandStrip'
import { QuizReportPreview } from './QuizReportPreview'

interface Props {
    web: WebViewDto
    quiz: QuizDto
    canEdit: boolean
    isSignedOff: boolean
    onEdit: () => void
}

const SECONDARY_BUTTON = 'inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700'
const COLUMN_HEADER = 'mt-4 grid pb-2 border-b border-slate-200 text-xs font-medium uppercase tracking-wider text-slate-500 dark:border-slate-700 dark:text-slate-400'
const ID_CODE = 'font-mono text-[12px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-200'
const ADVICE = 'text-sm leading-relaxed text-slate-600 dark:text-slate-300'
const MUTED = 'text-sm text-slate-400 dark:text-slate-500'

/** Only an absolute http(s) link is rendered as one; the save validator allows nothing else. */
const isWebLink = (url: string) => /^https?:\/\//i.test(url)

function SectionHeader({ title, count, note }: { title: string; count?: number; note?: string }) {
    return (
        <div className="flex items-baseline justify-between gap-6">
            <h3 className="text-base font-semibold text-slate-900 dark:text-white">
                {title}{count != null && <span className="ml-1 text-sm font-normal tabular-nums text-slate-500 dark:text-slate-400">{count}</span>}
            </h3>
            {note && <p className="text-xs text-slate-500 dark:text-slate-400">{note}</p>}
        </div>
    )
}

function Stat({ label, children }: { label: string; children: ComponentChildren }) {
    return (
        <div>
            <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</dt>
            <dd className="mt-1 text-sm font-medium text-slate-900 dark:text-white">{children}</dd>
        </div>
    )
}

function ItemId({ id, label }: { id: string; label: string }) {
    return <><code className={ID_CODE}>{id}</code><CopyButton text={id} label={label} /></>
}

function ResultRow({ title, hint, children }: { title: string; hint: string; children: ComponentChildren }) {
    return (
        <div className="grid grid-cols-[15rem_minmax(0,1fr)] gap-x-8 py-5">
            <div><p className="text-sm font-medium text-slate-900 dark:text-white">{title}</p><p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{hint}</p></div>
            {children}
        </div>
    )
}

/** The saved quiz, as the Quiz tab shows it when nobody is editing. */
export function QuizReadView({ web, quiz, canEdit, isSignedOff, onEdit }: Props) {
    const answered = web.Download?.QuizSubmissionCount ?? 0
    const dimensionName = new Map(quiz.Dimensions.map(dimension => [dimension.Id, dimension.Name || dimension.Id]))
    const questionCount = (dimensionId: string) => quiz.Questions.filter(question => question.DimensionId === dimensionId).length
    const [previewingReport, setPreviewingReport] = useState(false)

    return (
        <section className="rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
            <QuizHeading web={web}>
                <div className={`shrink-0 flex items-center ${isSignedOff ? 'gap-4' : 'gap-2'}`}>
                    <button type="button" onClick={() => setPreviewingReport(true)} aria-expanded={previewingReport} aria-controls="report-preview" className={SECONDARY_BUTTON}><FileText className="w-4 h-4" />Preview report</button>
                    {isSignedOff
                        ? canEdit && <p className="inline-flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400"><Lock className="w-4 h-4" />Reopen the web to edit</p>
                        : canEdit && <EditButton onClick={onEdit} />}
                </div>
            </QuizHeading>
            {previewingReport && <QuizReportPreview web={web} quiz={quiz} onClose={() => setPreviewingReport(false)} />}

            <dl className="bg-slate-50 border-y border-slate-200 px-6 py-4 flex flex-wrap gap-x-12 gap-y-3 dark:bg-slate-900 dark:border-slate-700">
                <Stat label="Scale">1-{quiz.ScaleMax}</Stat>
                <Stat label="Questions">{quiz.Questions.length}</Stat>
                <Stat label="Dimensions">{quiz.Dimensions.length}</Stat>
                <Stat label="Bands">{quiz.Bands.length}, scored out of 100</Stat>
                <Stat label="Ask for details">{quiz.DetailsPlacement === 'Before' ? 'Before the questions' : 'After the questions'}</Stat>
                <Stat label="Answers">
                    {answered > 0
                        ? <span className="inline-flex items-center gap-1.5">{answered} <Lock className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" /><span className="font-normal text-slate-600 dark:text-slate-300">structure locked</span></span>
                        : 0}
                </Stat>
            </dl>

            <div className="px-6 pt-8 pb-8 space-y-12">
                <div>
                    <SectionHeader title="Questions" count={quiz.Questions.length} note="In the order people see them" />
                    {quiz.Questions.length === 0 ? <p className={`mt-4 ${MUTED}`}>No questions yet.</p> : (
                        <>
                            <div className={`${COLUMN_HEADER} grid-cols-[2rem_minmax(0,1fr)_13rem_11rem] gap-x-6`}><div>#</div><div>Question</div><div>Dimension</div><div>Options</div></div>
                            <ol className="divide-y divide-slate-200 dark:divide-slate-700">
                                {quiz.Questions.map((question, index) => (
                                    <li key={question.Id} className="grid grid-cols-[2rem_minmax(0,1fr)_13rem_11rem] gap-x-6 py-5">
                                        <div className="text-sm font-medium tabular-nums text-slate-500 dark:text-slate-400">{index + 1}</div>
                                        <div className="min-w-0">
                                            {question.Text
                                                ? <p className="text-sm font-medium text-slate-900 max-w-[78ch] dark:text-white">{question.Text}</p>
                                                : <p className={MUTED}>No wording yet</p>}
                                            {question.HelpText && <p className="mt-1 text-sm text-slate-500 max-w-[78ch] dark:text-slate-400">{question.HelpText}</p>}
                                            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600 dark:text-slate-300">
                                                <span className="inline-flex items-center gap-1"><ItemId id={question.Id} label="question ID" /></span>
                                                <span className="inline-flex items-center gap-1.5">
                                                    <span className="tabular-nums text-slate-500 dark:text-slate-400">1</span>{question.LowLabel}
                                                    <span className="text-slate-400 mx-1 dark:text-slate-500"><ArrowRight className="w-3 h-3" /></span>
                                                    <span className="tabular-nums text-slate-500 dark:text-slate-400">{quiz.ScaleMax}</span>{question.HighLabel}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="text-sm text-slate-700 dark:text-slate-300">{dimensionName.get(question.DimensionId) ?? <span className={MUTED}>No dimension</span>}</div>
                                        <div className="flex flex-col items-start gap-1.5">
                                            {question.AllowsNotApplicable && (
                                                <>
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap dark:bg-slate-700 dark:text-slate-300"><Ban className="w-3 h-3" />Not applicable allowed</span>
                                                    {question.NotApplicableLabel && <span className="text-xs text-slate-500 dark:text-slate-400">&ldquo;{question.NotApplicableLabel}&rdquo;</span>}
                                                </>
                                            )}
                                            {question.IsReverseScored && <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 whitespace-nowrap dark:bg-slate-700 dark:text-slate-300">Reverse scored</span>}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </>
                    )}
                </div>

                <div>
                    <SectionHeader title="Dimensions" count={quiz.Dimensions.length} note="Advice for a low, mid or high score in each" />
                    {quiz.Dimensions.length === 0 ? <p className={`mt-4 ${MUTED}`}>No dimensions yet.</p> : (
                        <>
                            <div className={`${COLUMN_HEADER} grid-cols-[15rem_repeat(3,minmax(0,1fr))] gap-x-8`}><div>Dimension</div><div>Low</div><div>Mid</div><div>High</div></div>
                            <div className="divide-y divide-slate-200 dark:divide-slate-700">
                                {quiz.Dimensions.map(dimension => {
                                    const count = questionCount(dimension.Id)
                                    return (
                                        <div key={dimension.Id} className="grid grid-cols-[15rem_repeat(3,minmax(0,1fr))] gap-x-8 py-5">
                                            <div>
                                                <p className="text-sm font-medium text-slate-900 dark:text-white">{dimension.Name || <span className={MUTED}>No name yet</span>}</p>
                                                <div className="mt-1.5 flex items-center gap-1"><ItemId id={dimension.Id} label="dimension ID" /></div>
                                                <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{count} {count === 1 ? 'question' : 'questions'}</p>
                                            </div>
                                            {[dimension.LowAdvice, dimension.MidAdvice, dimension.HighAdvice].map((advice, tier) => (
                                                <p key={tier} className={advice ? ADVICE : MUTED}>{advice || '—'}</p>
                                            ))}
                                        </div>
                                    )
                                })}
                            </div>
                        </>
                    )}
                </div>

                <div>
                    <SectionHeader title="Bands" count={quiz.Bands.length} note="Scored out of 100" />
                    {quiz.Bands.length === 0 ? <p className={`mt-4 ${MUTED}`}>No bands yet.</p> : (
                        <>
                            <div className="mt-4"><QuizBandStrip bands={quiz.Bands} /></div>
                            <div className={`${COLUMN_HEADER} grid-cols-[15rem_6rem_repeat(2,minmax(0,1fr))] gap-x-8`}><div>Band</div><div>Range</div><div>What this means</div><div>What to do</div></div>
                            <div className="divide-y divide-slate-200 dark:divide-slate-700">
                                {quiz.Bands.map(band => (
                                    <div key={band.Id} className="grid grid-cols-[15rem_6rem_repeat(2,minmax(0,1fr))] gap-x-8 py-5">
                                        <div>
                                            <p className="text-sm font-medium text-slate-900 dark:text-white">{band.Name || <span className={MUTED}>No name yet</span>}</p>
                                            <div className="mt-1.5 flex items-center gap-1"><ItemId id={band.Id} label="band ID" /></div>
                                        </div>
                                        <p className="text-sm tabular-nums text-slate-700 dark:text-slate-300">{band.MinScore}-{band.MaxScore}</p>
                                        <p className={band.WhatThisMeans ? ADVICE : MUTED}>{band.WhatThisMeans || '—'}</p>
                                        <p className={band.WhatToDo ? ADVICE : MUTED}>{band.WhatToDo || '—'}</p>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div>
                    <SectionHeader title="Results" />
                    <div className="mt-4 border-t border-slate-200 divide-y divide-slate-200 dark:border-slate-700 dark:divide-slate-700">
                        <ResultRow title="Button" hint="On the results page, in the email and in the report">
                            <div>
                                <p className="text-sm font-medium text-slate-900 dark:text-white">{quiz.ButtonLabel || 'Book a call'}</p>
                                {!quiz.ButtonUrl
                                    ? <p className={`mt-1 ${MUTED}`}>No link yet</p>
                                    : isWebLink(quiz.ButtonUrl)
                                        ? <a href={quiz.ButtonUrl} target="_blank" rel="noopener noreferrer" className="mt-1 inline-flex items-center gap-1.5 text-sm text-slate-600 underline decoration-slate-300 underline-offset-2 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white">{quiz.ButtonUrl}<ExternalLink className="w-3.5 h-3.5" /></a>
                                        : <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{quiz.ButtonUrl}</p>}
                            </div>
                        </ResultRow>
                        <ResultRow title="Report sections" hint="In the PDF report, after the plan">
                            {quiz.ReportSections.length === 0 ? <p className={MUTED}>None</p> : (
                                <div className="space-y-5 max-w-[78ch]">
                                    {quiz.ReportSections.map(section => (
                                        <div key={section.Id}>
                                            <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{section.Title}</p>
                                            <p className="mt-1 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{section.Text}</p>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </ResultRow>
                        <ResultRow title="Disclaimer" hint="At the end of the report">
                            {quiz.Disclaimer
                                ? <p className="text-sm leading-relaxed text-slate-600 max-w-[78ch] dark:text-slate-300">{quiz.Disclaimer}</p>
                                : <p className={MUTED}>None</p>}
                        </ResultRow>
                    </div>
                </div>
            </div>
        </section>
    )
}
